import { ref, computed, provide, inject } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import axios from "axios";
import { useColorTheme } from "Admin/js/Mobile/Composables/useColorTheme";
import { useTheme } from "vuetify";
import { provideMobileTour } from "Admin/js/Mobile/Composables/useMobileTour";

const symbol = Symbol("mobileUser");

function useUserState() {
    const userState = ref(null);
    const userProfile = ref(null);
    const userProfiles = ref([]);
    const invitationAccepted = ref(null);
    const isLoadingUser = ref(false);
    const isRemoteSignup = ref(false);
    const theme = useTheme();
    const { initialize, saveTheme } = useColorTheme(theme);
    const { isLoading, manageAsyncOperation } = useAsyncOperation();
    const tourState = provideMobileTour();

    function fetchUserState() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.patch("/user-states");
            userState.value = data ?? {};
            tourState.lastTourIndex.value = data?.mobile_tour_index ?? 0;
        });
    }
    function fetchUserData(noLoader = false) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get("/user");
                userProfile.value = data?.profile ?? null;
                isRemoteSignup.value = data?.is_remote_signup ?? false;
            },
            null,
            (loading) => {
                if (!noLoader) {
                    isLoadingUser.value = loading;
                }
            },
        );
    }

    function fetchUserProfiles() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/user-profiles");
            userProfiles.value = data ?? [];
        });
    }

    const userProfilesMap = computed(() => {
        const map = new Map();
        for (const profile of userProfiles.value) {
            if (profile?.id != null) {
                map.set(Number(profile.id), profile);
            }
        }
        return map;
    });

    function getUserProfileById(id) {
        return userProfilesMap.value.get(Number(id)) ?? null;
    }

    function fetchInvitationAccepted() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/invitations/accepted");
            invitationAccepted.value = data?.data?.[0] ?? null;
        });
    }

    function fetchColorTheme() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/color-theme/mobile");
            const colorTheme = data?.data;
            if (colorTheme) {
                initialize();
                saveTheme(colorTheme);
            }
        });
    }

    function updatePersonalProfile(payload) {
        return manageAsyncOperation(async () => {
            const { data } = await axios.patch(
                `/profiles/${userProfile.value.id}`,
                payload,
            );
            const updatedProfile = data?.data;

            Object.keys(payload).forEach((key) => {
                userProfile.value[key] = updatedProfile[key];
            });
            return true;
        });
    }

    function updatePersonalProfileAvatar(avatar) {
        return manageAsyncOperation(async () => {
            const { data } = await axios.post(
                `/profiles/${userProfile.value.id}/avatar`,
                { avatar },
            );
            userProfile.value.avatar_url = data?.data?.url ?? avatar;
            return true;
        });
    }

    function updateOrganization(payload) {
        const organizationId = userProfile.value?.organization?.id;
        return manageAsyncOperation(async () => {
            const { data } = await axios.patch(
                `/organizations/${organizationId}`,
                payload,
            );
            const updatedOrganization = data?.data;
            Object.keys(payload).forEach((key) => {
                userProfile.value.organization[key] = updatedOrganization[key];
            });
            return true;
        });
    }

    function updateOrganizationAvatar(avatar) {
        const organizationId = userProfile.value?.organization?.id;
        return manageAsyncOperation(async () => {
            const { data } = await axios.post(
                `/organizations/${organizationId}/avatar`,
                { avatar },
            );
            userProfile.value.organization.avatar_url = data.data ?? avatar;
            return true;
        });
    }

    return {
        userState,
        userProfile,
        userProfiles,
        userProfilesMap,
        getUserProfileById,
        invitationAccepted,
        isLoading,
        isLoadingUser,
        isRemoteSignup,
        tourState,
        fetchUserState,
        fetchUserData,
        fetchUserProfiles,
        fetchInvitationAccepted,
        updatePersonalProfile,
        updatePersonalProfileAvatar,
        updateOrganization,
        updateOrganizationAvatar,
        fetchColorTheme,
    };
}

export function provideMobileUser() {
    const state = useUserState();
    provide(symbol, state);
    return state;
}

export function useMobileUser() {
    return inject(symbol);
}
